import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { PageShell, Heading, Text, Button, buttonClasses } from "@/components/ui";

/**
 * OPS-8: a render error anywhere below shows a friendly, translated page instead of
 * unmounting the whole app to a blank screen. The error itself is logged, never shown.
 */
function CrashPage() {
  const t = useTranslations("errors");
  return (
    <PageShell data-testid="app-error-page" role="alert">
      <Heading as="h1" level="page">
        {t("pageCrashedTitle")}
      </Heading>
      <Text tone="muted" className="mt-3">
        {t("pageCrashedBody")}
      </Text>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button variant="primary" onClick={() => window.location.reload()}>
          {t("pageCrashedReload")}
        </Button>
        <a href="/" className={buttonClasses({ variant: "secondary" })}>
          {t("pageCrashedHome")}
        </a>
      </div>
    </PageShell>
  );
}

type State = { failed: boolean };

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[app] render error", error, info.componentStack);
  }

  render() {
    return this.state.failed ? <CrashPage /> : this.props.children;
  }
}
